import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bell } from "lucide-react";
import api from "../lib/api";
import { ensurePushIfGranted } from "../lib/push";

const KEY = "hogtide_last_lead_seen";
const TOKEN = "hogtide_admin_token";

export default function OwnerNotifier() {
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN));
  const ctxRef = useRef(null);

  const chime = useCallback(() => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = ctxRef.current || (ctxRef.current = new Ctx());
      if (ctx.state === "suspended") ctx.resume();
      [880, 1320].forEach((f, i) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "sine";
        o.frequency.value = f;
        o.connect(g);
        g.connect(ctx.destination);
        const t = ctx.currentTime + i * 0.18;
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(0.25, t + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
        o.start(t);
        o.stop(t + 0.36);
      });
    } catch (e) {}
  }, []);

  // Track login/logout so the notifier follows the owner everywhere on the site.
  useEffect(() => {
    const check = () => setAuthed(!!localStorage.getItem(TOKEN));
    window.addEventListener("storage", check);
    const t = setInterval(check, 3000);
    return () => {
      window.removeEventListener("storage", check);
      clearInterval(t);
    };
  }, []);

  useEffect(() => {
    if (!authed) return;
    if (!localStorage.getItem(KEY)) localStorage.setItem(KEY, new Date().toISOString());
    ensurePushIfGranted();
    let stopped = false;
    const poll = async () => {
      const since = localStorage.getItem(KEY) || new Date().toISOString();
      try {
        const leads = await api.leadsRecent(since);
        if (stopped || !leads || !leads.length) return;
        localStorage.setItem(KEY, leads[0].created_at);
        setCount((c) => c + leads.length);
        chime();
        leads.slice(0, 3).forEach((l) => {
          toast(`🎣 New lead: ${l.name}`, {
            description: `${l.source}${l.trip ? ` · ${l.trip}` : ""} · ${l.phone || l.email}`,
            duration: 12000,
            action: { label: "View", onClick: () => navigate(`/admin?lead=${l.id}`) },
          });
        });
      } catch (e) {
        // ignore transient/401 errors; keep polling
      }
    };
    poll();
    const iv = setInterval(poll, 15000);
    return () => {
      stopped = true;
      clearInterval(iv);
    };
  }, [authed, chime, navigate]);

  if (!authed || count === 0) return null;
  return (
    <button
      data-testid="owner-notify-bell"
      onClick={() => {
        setCount(0);
        navigate("/admin");
      }}
      className="fixed bottom-6 left-6 z-[60] grid place-items-center h-14 w-14 rounded-full bg-[#f5a524] text-[#0a1f33] shadow-xl hover:scale-105 transition-transform"
      title={`${count} new lead${count > 1 ? "s" : ""}`}
    >
      <Bell className="h-6 w-6" />
      <span className="absolute -top-1 -right-1 grid place-items-center min-w-[22px] h-[22px] rounded-full bg-red-600 text-white text-xs font-bold px-1">
        {count}
      </span>
    </button>
  );
}
